import debounce from 'lodash/debounce';
import {
  ForwardRefRenderFunction,
  forwardRef,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import useResizeObserver from '../../../../../../common/shared/hooks/useResizeObserver';
import {useAddWindowEventListener} from '../../../../../../common/useAddWindowEventListener.hook';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {TEXT_VIEWER_DARK_THEME_LIST} from '../constant/viewerConstant';
import {useCheckTextViewerNeedsLoadContent} from '../hook/useCheckTextViewerNeedsLoadContent.hook';
import {useTextViewerCoreOption} from '../hook/useTextViewerCoreOption.hook';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {HtmlScrollViewer} from '../presentational/HtmlScrollViewer';
import {ScrollViewerScrollPositionAdjuster} from '../presentational/ScrollViewerScrollPositionAdjuster';
import {TextViewerPageCounter} from '../presentational/TextViewerPageCounter';
import {goToPrevChapter} from '../state/textViewerSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {FrameSize} from '../type/FrameSize.interface';
import {ScrollTextViewerBottomContainer} from './ScrollTextViewerBottomContainer';
import {ScrollTextViewerProgressContainer} from './ScrollTextViewerProgressContainer';
import {ScrollTextViewerTopContainer} from './ScrollTextViewerTopContainer';

const _ScrollTextViewerContainer: ForwardRefRenderFunction<
  HTMLDivElement,
  {
    seriesId: number;
    productId: number;
    isCmsPreview: boolean;
    children?: ReactNode;
    wrapperRef?: RefObject<HTMLDivElement>;
  }
> = (
  {seriesId, productId, isCmsPreview, children, wrapperRef},
  forwardedRef
) => {
  const {isMobile} = requestContext.requestInfo.display;

  const {
    isViewerEnd,
    isDarkColor,
    htmlInfo,
    chapterCount,
    currentChapterId,
    isHandlingSeekBar,
  } = useTypedSelector(state => ({
    isViewerEnd: state.textViewer.viewData[productId]?.isViewerEnd,
    isDarkColor: TEXT_VIEWER_DARK_THEME_LIST.includes(
      state.textViewer.settings.theme
    ),
    htmlInfo: state.textViewer.viewData[productId]?.currentHtmlInfo,
    chapterCount: state.textViewer.viewData[productId]?.chapterCount,
    currentChapterId: state.textViewer.viewData[productId]?.currentChapterId,
    isHandlingSeekBar: state.textViewer.viewData[productId]?.isHandlingSeekBar,
  }));

  useEffect(() => {
    // 뷰어엔드로 스크롤을 통해 진입했을 때 스크롤 위치를 항상 최상위로 고정합니다.
    if (isViewerEnd) {
      window.scroll(0, 0);
    }
  }, [isViewerEnd]);

  const [frameSize, setFrameSize] = useState<FrameSize>({
    width: 0,
    height: 0,
  });
  const {ref: viewerWrapperRef} = useResizeObserver<HTMLDivElement>({
    onResize: ({width, height}) => {
      if (!width || !height) {
        return;
      }
      setFrameSize({width, height: document.documentElement.clientHeight});
    },
  });

  const option = useTextViewerCoreOption({
    size: frameSize,
    isMobile,
  });
  useCheckTextViewerNeedsLoadContent({productId, size: frameSize});

  const dispatch = useTypedDispatch();

  // 이전에 스크롤 위치를 기억해서 원복시키는 로직이 있습니다.
  // 근데 해당 로직이 수행되면 스크롤 이벤트가 발생해서 뷰어 메뉴를 즉시 닫아 버리는 문제가 있습니다.
  // 그래서 첫 번째 스크롤 이벤트를 막고 해당 뷰어 메뉴가 즉시 닫히는 문제를 해결합니다.
  const isBlockFirstScrollEventRef = useRef(true);
  const onScroll = useMemo(
    () =>
      debounce(() => {
        if (!isBlockFirstScrollEventRef.current && !isHandlingSeekBar) {
          dispatch(setIsViewerMenuExposed({isExposed: false}));
        }
        isBlockFirstScrollEventRef.current = false;
      }, 50),
    [dispatch, isHandlingSeekBar]
  );

  useAddWindowEventListener({type: 'scroll', listener: onScroll});

  const {shouldUseNonstopWatching} = useViewerMenuHandler({
    isMobile,
  });

  useEffect(() => {
    if (!chapterCount || currentChapterId === undefined) {
      return;
    }
    // 다음 회차가 있는 상태의 정주행 모드로 변경될 때는 컨텐츠의 마지막 페이지가 보이도록 조정합니다.
    if (shouldUseNonstopWatching) {
      const lastChapterId = chapterCount - 1;
      if (currentChapterId > lastChapterId) {
        dispatch(goToPrevChapter({productId}));
      }
    }
  }, [
    chapterCount,
    currentChapterId,
    shouldUseNonstopWatching,
    dispatch,
    productId,
  ]);

  return (
    <div
      className="min-h-full w-full"
      style={{
        backgroundColor: isViewerEnd ? undefined : option.backgroundColor,
      }}
    >
      <div
        ref={viewerWrapperRef}
        className="mx-auto min-h-full w-full"
        style={{
          maxWidth: isMobile
            ? CONTENTS_MOBILE_MAX_WIDTH
            : CONTENTS_PC_MAX_WIDTH,
        }}
      >
        {htmlInfo && !isViewerEnd && (
          <HtmlScrollViewer
            ref={forwardedRef}
            wrapperRef={wrapperRef}
            htmlInfo={htmlInfo}
            option={option}
            productId={productId}
          />
        )}
        {!shouldUseNonstopWatching && !!isViewerEnd && (
          <div className="flex min-h-screen items-center">{children}</div>
        )}
        <ScrollTextViewerTopContainer productId={productId} />
        {!isViewerEnd && (
          <ScrollTextViewerBottomContainer
            productId={productId}
            isDarkColor={isDarkColor}
          />
        )}
        <ScrollTextViewerProgressContainer
          seriesId={seriesId}
          productId={productId}
          isCmsPreview={isCmsPreview}
          wrapperRef={wrapperRef}
        />
        <TextViewerPageCounter productId={productId} size={frameSize} />
        <ScrollViewerScrollPositionAdjuster
          productId={productId}
          size={frameSize}
          wrapperRef={wrapperRef}
        />
      </div>
    </div>
  );
};

export const ScrollTextViewerContainer = forwardRef(_ScrollTextViewerContainer);

const CONTENTS_MOBILE_MAX_WIDTH = 600;
const CONTENTS_PC_MAX_WIDTH = 720;
